import React, { useCallback, useMemo } from 'react';
import { List, Box } from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  restrictToVerticalAxis,
  restrictToParentElement,
} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import TierRow from './TierRow';

function TierList({
  tiers,
  onUpdate,
  onRemove,
  onReorder,
  validationErrors,
}) {
  const getTierIndex = useCallback(
    (tierId) => (tiers || []).findIndex((t) => t.id === tierId),
    [tiers]
  );

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const sortedTiers = useMemo(
    () => [...(tiers || [])].sort((a, b) => (b.order ?? 0) - (a.order ?? 0)),
    [tiers]
  );

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event;
      if (active.id === over?.id) return;
      const oldIndex = sortedTiers.findIndex((t) => t.id === active.id);
      const newIndex = sortedTiers.findIndex((t) => t.id === over?.id);
      if (oldIndex === -1 || newIndex === -1) return;
      const reordered = arrayMove(sortedTiers, oldIndex, newIndex);
      onReorder(reordered);
    },
    [sortedTiers, onReorder]
  );

  const sortableIds = useMemo(() => sortedTiers.map((t) => t.id), [sortedTiers]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToVerticalAxis, restrictToParentElement]}
    >
      <SortableContext
        items={sortableIds}
        strategy={verticalListSortingStrategy}
      >
        <List sx={{ p: 0 }}>
          {sortedTiers.map((tier) => (
            <TierRow
              key={tier.id}
              tier={tier}
              tierIndex={getTierIndex(tier.id)}
              onUpdate={onUpdate}
              onRemove={onRemove}
              canDelete={tiers.length > 1}
              validationErrors={validationErrors}
            />
          ))}
        </List>
      </SortableContext>
    </DndContext>
  );
}

export default TierList;
